import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { useKeyPress } from '@/hooks/useKeyPress'
import { queryClient } from '@/lib/queryClient'
import { GoBackButton } from '@/shared/components'
import {
  Button,
  MotionDiv,
  SlideInScreen,
  StickyContainer,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from '@/shared/ui'

import { getContractors } from '../../../api'
import { CSVContractorsList } from '../CSVContractorsList'
import { CSVFailedContractorsList } from '../CSVFailedContractorsList'

async function uploadContractors(ids: string[]) {
  console.log(ids)

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true)
    }, 1000)
  })
}

type Props = {
  onBack: () => void
}

export const BulkUploadContractorsSelect = ({ onBack }: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const contractorsQuery = useQuery({
    queryKey: [queryKeys.getContractors],
    queryFn: () => getContractors(),
    select: (data) => data?.data,
  })

  const { mutateAsync, isPending, isSuccess } = useMutation({
    mutationFn: uploadContractors,
  })

  const [selectedIdx, setSelectedIdx] = useState<string[]>([])

  const onContinue = async () => {
    await mutateAsync(selectedIdx)

    await queryClient.invalidateQueries({
      queryKey: [queryKeys.getContractors],
    })

    toast.success(
      intl.formatMessage({
        id: 'contractors.bulkUpload.success',
        defaultMessage: 'Contractors imported successfully',
      }),
    )

    navigate(BusinessRoute.ContractorsAll)
  }

  useKeyPress('Enter', () => onContinue())

  return (
    <>
      <GoBackButton onClick={onBack} />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            defaultMessage="Review and import contractor details"
            id="contractors.bulkUpload.select.title"
          />
        </Typography>

        <div className="p-2" />

        <Typography className="text-center">
          <FormattedMessage
            id="contractors.bulkUpload.select.subtitle"
            defaultMessage="Make sure contractor details are correct"
          />
        </Typography>

        <div className="p-6" />

        <Tabs defaultValue="valid">
          <TabsList variant="outline">
            <TabsTrigger
              disabled={contractorsQuery.data?.length === 0}
              size="fit"
              variant="outline"
              value="valid"
            >
              <FormattedMessage
                id="bulkPayments.tabs.valid"
                defaultMessage="Ready to import • {count}"
                values={{
                  count: contractorsQuery.data?.length ?? 0,
                }}
              />
            </TabsTrigger>
            <TabsTrigger
              disabled={false}
              size="fit"
              variant="outline"
              value="invalid"
            >
              <FormattedMessage
                id="bulkPayments.tabs.invalid"
                defaultMessage="Failed to import • {count}"
                values={{
                  count: 0,
                }}
              />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="valid">
            <MotionDiv className="pt-6" key={contractorsQuery.data?.length}>
              {contractorsQuery.isPending ? (
                <CSVContractorsList.Skeleton />
              ) : (
                <CSVContractorsList
                  selectedContractors={selectedIdx}
                  contractors={contractorsQuery.data ?? []}
                  onSelect={(selected) => setSelectedIdx(selected)}
                />
              )}
            </MotionDiv>
          </TabsContent>
          <TabsContent value="invalid">
            <MotionDiv className="pt-6" key={contractorsQuery.data?.length}>
              {contractorsQuery.isPending ? (
                <CSVContractorsList.Skeleton />
              ) : (
                <CSVFailedContractorsList
                  contractors={contractorsQuery.data ?? []}
                />
              )}
            </MotionDiv>
          </TabsContent>
        </Tabs>

        <StickyContainer>
          <Button
            loading={isPending || isSuccess}
            disabled={selectedIdx.length < 1 || isPending || isSuccess}
            onClick={() => onContinue()}
            width="full"
          >
            <FormattedMessage defaultMessage="Continue" id="action.continue" />
          </Button>
        </StickyContainer>
      </SlideInScreen>
    </>
  )
}
